import { Button, ButtonContainer, Heading, Layout, Form } from '@vwfs-bronson/bronson-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Notification,
    ValidatedSelect,
    ValidatedSelectItem,
    NotificationStatus,
    Spinner,
    useAnalyticsActionTracker,
    ValidatedTextarea,
} from '@cp-shared-19/frontend-ui';
import {
    Contract,
    ContractInfo,
    ContractType,
    getMyRequestEndpoint,
    MyRequest,
    OpenRequestRentingCategoriesSalesforce,
    OperatingLeaseFinancialDetails,
} from 'cpes-common';
import { useHistory } from 'react-router-dom';
import { dashboardPagePath } from 'components/navigation/paths';
import { Formik } from 'formik';
import { CpDataApi } from '../../../cp-xhr';
import { boundry, ContractRecalculationFormValues, getInitialValues } from './ContractRecalculationFormValues';
import { getContractRecalculationValidationSchema, parseMileageValue } from './Validation';
import { formatCpDate, trimAllValues } from '@cp-shared-19/common-utilities';
import { usePersonalDetails } from '../../my-profile/usePersonalDetails';
import { TranslationFormat, useTranslationWithFormatting } from 'localization/useTranslationWithFormatting';
import { MileageInput } from './MileageInput';

type ContractRecalculationFormUiProps = {
    contract: Contract;
    financialDetails: OperatingLeaseFinancialDetails;
    vinCode: string;
};

type ResetNoChangesNotificationProps = {
    durationValue: number;
    distanceValue: number;
    visible: boolean;
    onClear: () => void;
};

const ResetNoChangesNotification: React.FC<ResetNoChangesNotificationProps> = ({
    durationValue,
    distanceValue,
    visible,
    onClear,
}) => {
    const previousValuesRef = useRef({ durationValue, distanceValue });

    useEffect(() => {
        const durationChanged = previousValuesRef.current.durationValue !== durationValue;
        const distanceChanged = previousValuesRef.current.distanceValue !== distanceValue;

        if (visible && (durationChanged || distanceChanged)) {
            onClear();
        }

        previousValuesRef.current = { durationValue, distanceValue };
    }, [durationValue, distanceValue, visible, onClear]);

    return null;
};

export const ContractRecalculationForm: React.FC<ContractRecalculationFormUiProps> = ({
    contract,
    financialDetails,
    vinCode,
}) => {
    const { t, f } = useTranslationWithFormatting('contract-recalculation');
    const history = useHistory();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [noChangesNotificationVisible, setNoChangesNotificationVisible] = useState(false);
    const currentMileage =
        financialDetails.mileageLastRecalculation ?? (boundry.distance.minimum + boundry.distance.maximum) / 2;
    const currentDuration = Math.round(
        formatCpDate(financialDetails.endDate).diff(financialDetails.startDate, 'M', true),
    );
    const clearNoChangesNotification = useCallback(() => setNoChangesNotificationVisible(false), []);
    const { onAction: onSubmitSuccess } = useAnalyticsActionTracker('contractRecalculationOnSubmitSuccess');
    const cancel = useCallback((): void => {
        history.push(dashboardPagePath());
    }, [history]);
    const isFormCompleted = submitSuccess;

    const initialValues = useMemo(() => getInitialValues(currentMileage, currentDuration), [
        currentMileage,
        currentDuration,
    ]);
    const validationSchema = useMemo(
        () =>
            getContractRecalculationValidationSchema(t, currentDuration, currentMileage, value =>
                f(value, TranslationFormat.NUMBER),
            ),
        [t, currentDuration, currentMileage, f],
    );

    const durationSelectItems: ValidatedSelectItem[] = useMemo(
        () =>
            Array.from({ length: boundry.duration.maximum - currentDuration + 1 }, (_, index) => {
                const duration = currentDuration + index;
                return {
                    label: `${f(duration, TranslationFormat.NUMBER)} ${t('duration-unit')}`,
                    value: String(duration),
                    key: index,
                };
            }),
        [currentDuration, f, t],
    );

    const { data: myDetailsList } = usePersonalDetails();

    const getContractTypeFromGeneralCategory = (category: string): ContractType =>
        category === OpenRequestRentingCategoriesSalesforce.RECALCULATION
            ? ContractType.OPERATING_LEASE
            : ContractType.FINANCING;
    const getContractInfo = (contract: Contract, category: string, contractId?: string): ContractInfo | undefined => {
        if (contractId && contractId !== '0') {
            const { contractNumber, licensePlateNumber, contractType } = contract;
            return { licensePlate: licensePlateNumber, contractId: contractNumber, contractType };
        } else {
            return { contractType: getContractTypeFromGeneralCategory(category) };
        }
    };

    const onSubmit = (values: ContractRecalculationFormValues): void => {
        const normalizedNewDuration = Number(values.durationValue);
        const normalizedNewMileage = parseMileageValue(values.distanceValue);
        const durationChanged = normalizedNewDuration !== currentDuration;
        const mileageChanged = normalizedNewMileage !== currentMileage;
        const hasChanges = durationChanged || mileageChanged;
        const recalculationType = normalizedNewMileage < currentMileage ? 'reduction' : 'extension';

        if (!hasChanges) {
            setNoChangesNotificationVisible(true);
            setSubmitError(false);
            setSubmitSuccess(false);
            return;
        }

        setIsSubmitting(true);
        setSubmitError(false);
        setSubmitSuccess(false);
        setNoChangesNotificationVisible(false);

        let email = '';
        if (myDetailsList?.contact?.email) {
            email = myDetailsList?.contact?.email;
        }

        //It is requested by the business that the information must be received in Spanish
        const requestObj = {
            'Bastidor (VIN)': vinCode,
            'Tipo de recalculo (Recalculation Type)': recalculationType,
            'Tiempo (Period)': normalizedNewDuration,
            'Kilometros (Mileage)': normalizedNewMileage,
            'Nuevo Kilometraje (New Mileage)': mileageChanged,
            'Nueva Duracion (New Duration)': durationChanged,
            'Comentario (Comment)': values.comment,
        };
        const requestBody: MyRequest = {
            email: email,
            category: OpenRequestRentingCategoriesSalesforce.RECALCULATION,
            shortDescription: 'Recálculo operación Renting',
            request: JSON.stringify(requestObj),
            contractInfo: getContractInfo(
                contract,
                OpenRequestRentingCategoriesSalesforce.RECALCULATION,
                contract.contractNumber,
            ),
        };
        CpDataApi.post(getMyRequestEndpoint(), trimAllValues(requestBody))

            .then(() => {
                setSubmitSuccess(true);
                setSubmitError(false);
                const recalculationChangeArr = [];
                if (mileageChanged) {
                    recalculationChangeArr.push('mileage');
                }
                if (durationChanged) {
                    recalculationChangeArr.push('duration');
                }
                const recalculationChange = recalculationChangeArr.join(' & ');
                onSubmitSuccess(recalculationType, recalculationChange);
            })
            .catch(() => {
                setSubmitError(true);
                setSubmitSuccess(false);
            })
            .finally(() => setIsSubmitting(false));
    };

    return (
        <>
            <Heading level={4}>{t('form.headline')}</Heading>
            {isSubmitting && <Spinner fullPage={true} />}
            <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
                {formik => (
                    <Form onSubmit={formik.handleSubmit}>
                        <ResetNoChangesNotification
                            durationValue={Number(formik.values.durationValue)}
                            distanceValue={parseMileageValue(formik.values.distanceValue)}
                            visible={noChangesNotificationVisible}
                            onClear={clearNoChangesNotification}
                        />
                        <div className="u-mb">
                            <Notification
                                status={NotificationStatus.success}
                                headline={t('notification.success.headline')}
                                testId="submissionSuccess"
                                visible={submitSuccess}
                            >
                                {t('notification.success.text')}
                            </Notification>
                            <Notification
                                status={NotificationStatus.error}
                                headline={t('notification.error.headline')}
                                testId="submissionError"
                                visible={submitError}
                            >
                                {t('notification.error.text')}
                            </Notification>
                            <Notification
                                status={NotificationStatus.warning}
                                headline={t('notification.at-least-one-change.headline')}
                                testId="noChangesNotification"
                                visible={noChangesNotificationVisible}
                            >
                                {t('notification.at-least-one-change.text')}
                            </Notification>
                        </div>
                        <Layout>
                            <Layout.Item default="1/2" s="1/1">
                                <ValidatedSelect
                                    className="c-form-field--no-floating"
                                    label={t('form.duration-label')}
                                    name="durationValue"
                                    testId="durationValue"
                                    selectItems={durationSelectItems}
                                    disablePlaceholder={true}
                                    disabled={isFormCompleted ? true : undefined}
                                />
                            </Layout.Item>
                            <Layout.Item default="1/2" s="1/1">
                                <MileageInput
                                    className="c-form-field--no-floating"
                                    label={t('form.distance-label')}
                                    name={'distanceValue'}
                                    testId={'distanceValue'}
                                    addonText={t('distance-unit')}
                                    disabled={isFormCompleted ? true : undefined}
                                />
                            </Layout.Item>
                            <Layout.Item default="1/1">
                                <ValidatedTextarea
                                    className="c-form-field--no-floating"
                                    label={t('form.comments-label')}
                                    name={'comment'}
                                    testId={'comment'}
                                    id={'comment'}
                                    placeholder={t('form.input-placeholder')}
                                    disabled={isFormCompleted ? true : undefined}
                                />
                            </Layout.Item>
                            <Layout.Item default="1/1">
                                {isFormCompleted ? (
                                    <ButtonContainer className="u-mb-small" center>
                                        <Button testId="goToDashboardButton" type="button" onClick={cancel}>
                                            {t('button.go-to-dashboard')}
                                        </Button>
                                    </ButtonContainer>
                                ) : (
                                    <ButtonContainer className="u-mb-small" center>
                                        <Button
                                            secondary
                                            onClick={cancel}
                                            testId="backButton"
                                            type="button"
                                            disabled={isSubmitting ? true : undefined}
                                        >
                                            {t('button.cancel')}
                                        </Button>
                                        <Button
                                            secondary
                                            testId="submitButton"
                                            type="submit"
                                            disabled={isSubmitting ? true : undefined}
                                        >
                                            {t('button.submit')}
                                        </Button>
                                    </ButtonContainer>
                                )}
                            </Layout.Item>
                        </Layout>
                    </Form>
                )}
            </Formik>
        </>
    );
};
